import { type TFunction } from "i18next";

import { cn } from "@/lib/utils";

import { ActionsCell } from "./ActionsCell";
import { MetricValue } from "./MetricValue";
import { SortableColumnHeader } from "./SortableColumnHeader";
import { TextValue } from "./TextValue";
import {
  COMPACT_HEADER_CLASS,
  METRIC_HEADER_WIDTH_CLASS,
  NUMBER_COLUMN_WIDTH_CLASS,
  NUMERIC_HEADER_CLASS,
} from "./constants";
import {
  type CommodityColumnDef,
  type CommodityDashboardStat,
  type CommodityListItem,
  type CurrentCountry,
  type EntityId,
  type UnitOfMeasure,
} from "./types";
import {
  getAverageLandSize,
  getCommodityTranslation,
  getDepositsCount,
  getEventsCount,
  getFarmersWithCommodity,
  getHarvestsCount,
  getLandWithCommoditySum,
  getQuantityDepositedLabel,
  getQuantityDepositedTotal,
  getQuantityHarvestedLabel,
  getQuantityHarvestedTotal,
  getQuantitySoldLabel,
  getQuantitySoldTotal,
} from "./utils";

interface CreateColumnsOptions {
  currentCountry?: CurrentCountry;
  currentLanguageCode: string;
  currentLanguageLabel: string;
  landUnitLabel?: string;
  onClickAddCommodity?: (commodity: CommodityListItem) => void;
  onClickRemoveCommodity?: (commodity: CommodityListItem) => void;
  showAll: boolean;
  statsMap: Map<EntityId, CommodityDashboardStat>;
  unitsOfMeasure: UnitOfMeasure[];
}

const NUMERIC_CELL_CLASS = cn(NUMBER_COLUMN_WIDTH_CLASS, "px-3 text-right");
const QUANTITY_CELL_CLASS = "min-w-32 px-3 text-right";

// A right-aligned metric column sorted numerically off the dashboard stat for the row.
const numericMetricColumn = (
  id: string,
  label: string,
  getValue: (stat?: CommodityDashboardStat) => number,
  statsMap: Map<EntityId, CommodityDashboardStat>
): CommodityColumnDef => ({
  id,
  accessorFn: row => getValue(statsMap.get(row.id)),
  header: ({ column }) => <SortableColumnHeader align="end" column={column} label={label} />,
  cell: ({ row }) => <MetricValue value={getValue(statsMap.get(row.original.id))} />,
  enableSorting: true,
  sortingFn: "basic",
  meta: {
    cellClassName: NUMERIC_CELL_CLASS,
    headerClassName: cn(METRIC_HEADER_WIDTH_CLASS, NUMERIC_HEADER_CLASS),
  },
});

// A multi-unit quantity column ("1,200.55 kg | 235 bag") sorted by its total quantity
// across units — mixing units is fine for ordering, not for display.
const quantityColumn = (
  id: string,
  label: string,
  getLabel: (stat: CommodityDashboardStat | undefined, units: UnitOfMeasure[]) => string,
  getTotal: (stat?: CommodityDashboardStat) => number,
  statsMap: Map<EntityId, CommodityDashboardStat>,
  unitsOfMeasure: UnitOfMeasure[]
): CommodityColumnDef => ({
  id,
  accessorFn: row => getTotal(statsMap.get(row.id)),
  header: ({ column }) => <SortableColumnHeader align="end" column={column} label={label} />,
  cell: ({ row }) => <TextValue value={getLabel(statsMap.get(row.original.id), unitsOfMeasure)} />,
  enableSorting: true,
  sortingFn: "basic",
  meta: {
    cellClassName: QUANTITY_CELL_CLASS,
    headerClassName: cn(METRIC_HEADER_WIDTH_CLASS, NUMERIC_HEADER_CLASS),
  },
});

const createColumns = (
  t: TFunction,
  {
    currentCountry,
    currentLanguageCode,
    currentLanguageLabel,
    landUnitLabel,
    onClickAddCommodity,
    onClickRemoveCommodity,
    showAll,
    statsMap,
    unitsOfMeasure,
  }: CreateColumnsOptions
): CommodityColumnDef[] => {
  const landSuffix = landUnitLabel ? ` (${landUnitLabel})` : "";

  const commodityNameColumn: CommodityColumnDef = {
    id: "commodity",
    // Manage mode sorts on the English name; the country view sorts on the localised name.
    accessorFn: row => (showAll ? row.name : getCommodityTranslation(row, currentLanguageCode)),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("commodities.list.column.commodity")} />
    ),
    cell: ({ row }) => (
      <TextValue
        className="font-medium text-foreground"
        value={
          showAll ? row.original.name : getCommodityTranslation(row.original, currentLanguageCode)
        }
      />
    ),
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-48 px-3",
      headerClassName: cn("min-w-48", COMPACT_HEADER_CLASS),
    },
  };

  if (showAll) {
    // Manage view: actions first (add/remove), then names, translation and units.
    return [
      {
        id: "addToCountry",
        header: () => null,
        cell: ({ row }) => (
          <ActionsCell
            commodity={row.original}
            currentCountry={currentCountry}
            onClickAddCommodity={onClickAddCommodity}
            onClickRemoveCommodity={onClickRemoveCommodity}
            t={t}
          />
        ),
        enableSorting: false,
        meta: {
          cellClassName: "px-3",
        },
      },
      commodityNameColumn,
      {
        id: "translations",
        header: () => t("commodities.list.column.translation", { language: currentLanguageLabel }),
        cell: ({ row }) => (
          <TextValue value={getCommodityTranslation(row.original, currentLanguageCode)} />
        ),
        enableSorting: false,
        meta: {
          cellClassName: "min-w-40 px-3",
          headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
        },
      },
      {
        id: "unitsOfMeasurement",
        accessorFn: row => row.units,
        header: () => t("commodities.list.column.unitsOfMeasurement"),
        cell: ({ row }) => <TextValue value={row.original.units} />,
        enableSorting: false,
        meta: {
          cellClassName: "min-w-32 px-3",
          headerClassName: cn("min-w-32", COMPACT_HEADER_CLASS),
        },
      },
    ];
  }

  // Country overview: every metric column is sortable; "Quantity sold" sits right of
  // "Quantity deposited".
  return [
    commodityNameColumn,
    numericMetricColumn(
      "farmersWithCommodity",
      t("commodities.list.column.farmersWithCommodity"),
      getFarmersWithCommodity,
      statsMap
    ),
    numericMetricColumn(
      "sumOfLandSize",
      `${t("commodities.list.column.sumOfLandSize")}${landSuffix}`,
      getLandWithCommoditySum,
      statsMap
    ),
    numericMetricColumn(
      "averageLandSize",
      `${t("commodities.list.column.averageLandSize")}${landSuffix}`,
      getAverageLandSize,
      statsMap
    ),
    numericMetricColumn(
      "depositsAtAggregator",
      t("commodities.list.column.depositsAtAggregator"),
      getDepositsCount,
      statsMap
    ),
    quantityColumn(
      "quantityDeposited",
      t("commodities.list.column.quantityDeposited"),
      getQuantityDepositedLabel,
      getQuantityDepositedTotal,
      statsMap,
      unitsOfMeasure
    ),
    quantityColumn(
      "quantitySold",
      t("commodities.list.column.quantitySold"),
      getQuantitySoldLabel,
      getQuantitySoldTotal,
      statsMap,
      unitsOfMeasure
    ),
    numericMetricColumn(
      "harvestsByFarmer",
      t("commodities.list.column.harvestsByFarmer"),
      getHarvestsCount,
      statsMap
    ),
    quantityColumn(
      "quantityHarvested",
      t("commodities.list.column.quantityHarvested"),
      getQuantityHarvestedLabel,
      getQuantityHarvestedTotal,
      statsMap,
      unitsOfMeasure
    ),
    numericMetricColumn("events", t("commodities.list.column.events"), getEventsCount, statsMap),
  ];
};

export { createColumns };
